// Email + password registration (linked from /register SSO landing); preserves ?e= / ?invite_id=
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { APP_NAME, USER_ROLES } from '../helpers/constants';
import {
  decryptRegistrationParams,
} from '../helpers/encryptionUtils';
import { registerUser, RegisterRequest } from '../services/authService';
import { encryptPassword } from '@/utils/crypto';
import {
  validatePasswordRequirements,
  isPasswordValid,
  type PasswordValidation,
} from '../helpers/passwordUtils';
import PasswordPolicyIndicator from '../components/PasswordPolicyIndicator';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';

// B-1: Only non-privileged roles may be self-assigned via the invite URL.
// Omitting USER_ROLES.ADMIN prevents a crafted ?role=admin param from granting
// admin access at registration time. Authorization is enforced server-side too,
// but defence-in-depth means the client must not send admin in the first place.
const ALLOWED_SELF_REGISTER_ROLES: string[] = [USER_ROLES.USER, USER_ROLES.SUPPORT];

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const EmailRegister: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true);
  const [urlParams, setUrlParams] = useState<{
    email: string;
    inviteCode: string;
    companyId: string;
    role: string;
  } | null>(null);
  const [loadingParams, setLoadingParams] = useState(false);

  let authContext;
  try {
    authContext = useAuth();
  } catch (err) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Authentication Error: {err instanceof Error ? err.message : 'Unknown error'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Please refresh the page or contact support.
        </Typography>
      </Box>
    );
  }

  const { isAuthenticated, user, register } = authContext;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleUrlParams = async () => {
      const encryptedParam = searchParams.get('e');

      if (encryptedParam) {
        setLoadingParams(true);
        try {
          const params = await decryptRegistrationParams(encryptedParam);
          setUrlParams(params);

          setFormData((prev) => ({
            ...prev,
            email: params.email,
          }));
        } catch (err) {
          console.error('Failed to decrypt URL parameters:', err);
          setError('Invalid registration link. Please contact support.');
        } finally {
          setLoadingParams(false);
        }
      }
    };

    handleUrlParams();
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === USER_ROLES.ADMIN) {
        navigate('/supportSpace');
      } else {
        navigate('/supportSpace');
      }
    }
  }, [isAuthenticated, navigate, user]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isPasswordValid(passwordValidation)) {
      newErrors.password = 'Password must meet all requirements';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof RegisterFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }

    if (field === 'password') {
      const validation = validatePasswordRequirements(value);
      setPasswordValidation(validation);
      if (formData.confirmPassword) {
        setPasswordsMatch(value === formData.confirmPassword);
      }
    } else if (field === 'confirmPassword') {
      setPasswordsMatch(value === formData.password);
    }
  };

  const isFormValid = (): boolean => {
    return (
      formData.name.trim().length >= 2 &&
      formData.email.trim().length > 0 &&
      isPasswordValid(passwordValidation) &&
      passwordsMatch &&
      formData.confirmPassword.length > 0
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const encryptedPassword = await encryptPassword(formData.password);

      const inviteFromQuery = searchParams.get('invite_id') || '';

      const registrationData: RegisterRequest = {
        invite_id: urlParams?.inviteCode || inviteFromQuery,
        name: formData.name.trim(),
        email_id: formData.email.trim(),
        password: encryptedPassword,
        confirm_password: encryptedPassword,
        encrypted: true,
        company_id: urlParams?.companyId || '',
        role: ALLOWED_SELF_REGISTER_ROLES.includes(urlParams?.role ?? '')
          ? urlParams!.role
          : 'user',
      };

      const response = await registerUser(registrationData);

      register(response);

      setSuccessMessage('Registration successful! Redirecting to support space...');

      setTimeout(() => {
        if (response.role === USER_ROLES.ADMIN) {
          navigate('/supportSpace');
        } else {
          navigate('/supportSpace');
        }
      }, 2000);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'msg' in err
            ? String((err as { msg?: string }).msg)
            : 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box
          component="img"
          src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
          alt={APP_NAME}
          sx={{
            height: 48,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{ fontWeight: 'bold' }}
        >
          Create Account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Create your account to get started
        </Typography>

        {loadingParams && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading registration details...
          </Alert>
        )}

        {urlParams && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Registration link detected. Email pre-filled from invitation.
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              disabled={loading}
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              disabled={loading}
              type="email"
              error={!!errors.email}
              helperText={
                urlParams?.email
                  ? 'Email from invitation link (cannot be changed)'
                  : errors.email
              }
              InputProps={{ readOnly: !!urlParams?.email }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              required
              disabled={loading}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordPolicyIndicator validation={passwordValidation} show={!!formData.password} />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              required
              disabled={loading}
              error={
                !!errors.confirmPassword ||
                (formData.confirmPassword.length > 0 && !passwordsMatch)
              }
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordMatchIndicator match={passwordsMatch} show={!!formData.confirmPassword} />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 0 }}
            disabled={loading || !isFormValid()}
          >
            {loading ? <CircularProgress size={24} /> : 'CREATE ACCOUNT'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link
            to={{ pathname: '/register', search: location.search }}
            style={{
              color: muiTheme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Back to sign-up options
          </Link>
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Already have an account?{' '}
          <Link
            to="/login"
            style={{
              color: muiTheme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign in here
          </Link>
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
      </Typography>
    </Box>
  );
};

export default EmailRegister;
